import axios, { AxiosInstance } from 'axios';

/**
 * 로그인 토큰(ENR-BUGFIX-125, 이 앱은 ENR-BUGFIX-127). 백엔드는 `Authorization: Bearer` 를 보고 사용자를 확인한다.
 * 토큰은 redux user 상태에 있고, 스토어를 직접 import 하면 순환 참조가 생겨
 * `store/authSession.ts` 가 읽는 함수와 401 처리 함수를 넣어 준다.
 */
let readToken: () => string | undefined = () => undefined;
let onUnauthorized: () => void = () => {};

export const setAuthTokenReader = (reader: () => string | undefined) => {
  readToken = reader;
};

export const setUnauthorizedHandler = (handler: () => void) => {
  onUnauthorized = handler;
};

export const getAuthToken = () => readToken() || '';

export const bearerHeader = (token: string): Record<string, string> =>
  token ? { Authorization: `Bearer ${token}` } : {};

export const authHeaders = () => bearerHeader(getAuthToken());

/** 401 이면 로그인 상태를 비운다. 오류는 그대로 다시 던진다 */
export const handleUnauthorizedError = (error: unknown) => {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    onUnauthorized();
  }
  return Promise.reject(error);
};

export const notifyUnauthorized = () => onUnauthorized();

/** axios 인스턴스에 토큰 헤더와 401 처리를 붙인다. 호출부가 직접 넣은 헤더가 우선한다 */
export const attachAuth = (instance: AxiosInstance) => {
  instance.interceptors.request.use(config => {
    const token = getAuthToken();
    if (token && !config.headers?.Authorization) {
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }
    return config;
  });
  instance.interceptors.response.use(
    response => response,
    handleUnauthorizedError
  );
  return instance;
};

/**
 * 토큰이 만료됐는지. PC 시계가 틀려도 맞게, 받은 시각(이 PC 기준)부터 유효 시간(`exp - iat`)이 지났는지로 본다.
 * 읽을 수 없는 토큰은 만료로 본다.
 */
export const isTokenExpired = (
  token: string,
  receivedAtMs: number | undefined,
  nowMs = Date.now()
) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const { exp, iat } = JSON.parse(json) as { exp?: number; iat?: number };
    if (typeof exp !== 'number' || typeof iat !== 'number') return true;
    if (!receivedAtMs) return false;
    return nowMs - receivedAtMs >= (exp - iat) * 1000;
  } catch {
    return true;
  }
};
